/**
 * View to create a deployment for a device group from code assigned to a product.
 */
import Radio from 'backbone.radio';
import Marionette from 'backbone.marionette';
import _ from 'underscore';
import Handlebars from 'handlebars';
import DialogInterface from '@/interfaces/dialog';
import DeploymentList from '@/views/modules/selectable_pre_deployment_list';
import template from '@/../templates/deployment-create.hbs';
import strings_common from 'amdi18n-loader!@/nls/common';
import strings_specific from 'amdi18n-loader!@/nls/dialogs';
import Api from '@/lib/api/api';

var strings = _.extend({}, strings_common, strings_specific);
var MAX_DESCRIPTION_LENGTH = 300;

var globalCh = Radio.channel('global');
var alertsCh = Radio.channel('alerts');

var get_human_readable_type = function(type) {
    switch (type) {
    case 'development_devicegroup':
        return strings.application_workspace;
    case 'factoryfixture_devicegroup':
        return strings.factory_fixture_device_group;
    case 'dut_devicegroup':
        return strings.dut_devicegroup;
    case 'production_devicegroup':
        return strings.production_device_group;
    case 'pre_dut_devicegroup':
        return strings.pre_dut_devicegroup;
    case 'pre_factoryfixture_devicegroup':
        return strings.pre_factoryfixture_devicegroup;
    case 'pre_production_devicegroup':
        return strings.pre_production_devicegroup;
    default:
        return strings.invalid_device_group_type;
    }
};

function create_promoted_code_list(product, deployments) {
    return new DeploymentList({
        collection: deployments,
        product: product
    });
}

var view = Marionette.View.extend(DialogInterface).extend({
    template : Handlebars.compile(template),
    events: {
        'submit form': 'newDeploymentSubmit',
        'keyup @ui.description' : 'validateDescription',
        'paste @ui.description': 'validateDescription',
        'change @ui.description': 'validateDescription',
        'mouseup @ui.description': 'validateDescription',
        'keyup @ui.totp_input': 'validateDescription',
        'mouseup @ui.device_group': 'device_group'
    },
    childViewEvents: {
        'choose:deployment' : 'chooseDeployment',
        'page': function (filter) {
            var list_view = this.getRegion('code_list').currentView;
            list_view.setFilter(filter);
        }
    },
    initialize: function(options) {
        this.destroyOnClose();
        this.focusFirstInput();
        this.mergeOptions(options, ['devicegroup', 'product', 'deployment']);
        // Attn: this.deployment is created in chooseDeployment
    },
    regions: {
        device_group: '.device-group',
        code_list: '.code-list',
        deployment_settings: '.deployment-settings',
        pagination_controls: '.pagination'
    },
    validateDescription: function() {
        if (this.ui.description.val() && this.deployment) {
            this.check_validation(
                this.ui.description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.description_length_counter,
                this.ui.submit_btn
            );
        } else {
            this.check_validation(
                this.ui.description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.description_length_counter,
                this.ui.submit_btn
            );
            this.ui.submit_btn.prop('disabled', true);
        }
        if ( this.devicegroup.requires_mfa() && this.ui.totp_input.val().length != 6 ) {
            this.ui.submit_btn.prop('disabled', true);
        }
    },
    ui: {
        devicegroup_type: '.device-group .type',
        devicegroup_name: '.device-group .name',
        devicegroup_device_count: '.device-group .count',
        name: '#create-name',
        description: '.deployment-settings .description',
        submit_btn : '.submit',
        load_spinner : '.loader',
        description_length_counter: '.description-counter',
        upgrade_option: 'input[name=upgrade-option]',
        totp_form: '.totp-form',
        totp_input: '#totp-input'
    },
    templateContext: function() {
        var name = this.devicegroup.get('name');
        var type = get_human_readable_type(this.devicegroup.type);
        var is_factory = false;
        var is_production = false;
        var is_pre_factory = false;
        var is_pre_production = false;
        var is_development = false;
        var is_dut = false;
        var is_pre_dut = false;
        var show_restart_options = false;
        var id = this.devicegroup.id;
        
        switch (this.devicegroup.type){
            case 'pre_factoryfixture_devicegroup':
                show_restart_options = true;
                is_pre_factory = true;
                break;
            case 'pre_production_devicegroup':
                is_pre_production = true;
                show_restart_options = true;
                break;
            case 'factoryfixture_devicegroup':
                show_restart_options = true;
                is_factory = true;
                break;
            case 'production_devicegroup':
                is_production = true;
                show_restart_options = true;
                break;
            case 'dut_devicegroup':
                is_dut = true;
                break;
            case 'pre_dut_devicegroup':
                is_pre_dut = true;
                break;
            default:
                show_restart_options = true;
                is_development = true;
        }

        return {
            deployment: this.getOption('deployment'),
            show_restart_options: show_restart_options,
            type,
            id,
            is_pre_factory,
            is_pre_production,
            is_factory,
            is_production,
            is_development,
            is_dut,
            is_pre_dut,
            name: name,
            count: this.devicegroup.counts.get('devices'),
            s: strings,
        };
    },
    chooseDeployment: function(deployment) {
        var self = this;
        this.ui.description.text(deployment.get('description'));
        this.deployment = deployment.clone();
        this.deployment.url = deployment.url();
        this.deployment.fetch({
            data: {
                'nocode': true
            },
            success: function() {
                self.validateDescription();
            }
        });
    },
    onRender: function() {
        this.validateDescription();
        const views_devicegroup_type = this.getOption('devicegroup') && this.getOption('devicegroup').type;
        const views_devicegroup = this.getOption('devicegroup');

        const view = create_promoted_code_list(this.product, this.collection);

        if ( !this.devicegroup.requires_mfa() ) this.ui.totp_form.hide();

        if (views_devicegroup_type) {
            switch (views_devicegroup_type) {
                case views_devicegroup.TYPES_MAP.development:
                    // If view has development devicegroup, only show deployments in the same product,
                    // that are promoted, and are from development_devicegroups
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        return is_same_product && is_development_devicegroup_deployment;
                    };
                    break;
                case views_devicegroup.TYPES_MAP.pre_factoryfixture:
                    // If view has pre_factoryfixture, only show deployments in the same product,
                    // that are promoted, and are from pre_factoryfixture_devicegroups
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_pre_factoryfixture_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.pre_factoryfixture;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        const is_factoryfixture_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.factoryfixture;
                        return is_same_product && (is_development_devicegroup_deployment || is_pre_factoryfixture_devicegroup_deployment || is_factoryfixture_devicegroup_deployment);
                    };
                    break;
                case views_devicegroup.TYPES_MAP.pre_production:
                    // If view has pre_production, only show deployments in the same product,
                    // that are promoted, and are from pre_production OR development_devicegroup
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_pre_production_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.pre_production;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        const is_production_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.production;
                        return is_same_product && (is_development_devicegroup_deployment || is_pre_production_devicegroup_deployment || is_production_devicegroup_deployment);
                    };
                    break;
                case views_devicegroup.TYPES_MAP.factoryfixture:
                    // If view has development devicegroup, only show deployments in the same product,
                    // that are promoted, and are from pre_factoryfixture OR factoryfixture
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_promoted = deployment.get('flagged') === true;
                        const is_pre_factoryfixture_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.pre_factoryfixture;
                        const is_factoryfixture_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.factoryfixture;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        return is_same_product && is_promoted && (is_development_devicegroup_deployment || is_factoryfixture_devicegroup_deployment || is_pre_factoryfixture_devicegroup_deployment);
                    };
                    break;
                case views_devicegroup.TYPES_MAP.production:
                    // If view has development devicegroup, only show deployments in the same product,
                    // that are promoted, and are from pre_production, production_devicegroup, development_devicegroup
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_promoted = deployment.get('flagged') === true;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        const is_pre_production_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.pre_production;
                        const is_production_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.production;

                        return is_same_product && is_promoted && (is_development_devicegroup_deployment || is_pre_production_devicegroup_deployment || is_production_devicegroup_deployment);
                    };
                    break;
                case views_devicegroup.TYPES_MAP.dut:
                    // If view is dut devicegroup, only show deployments in the same product,
                    // that are promoted, and are from pre_dut, dut, development_devicegroup
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_promoted = deployment.get('flagged') === true;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        const is_pre_dut_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.pre_dut;
                        const is_dut_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.dut;

                        return is_same_product && is_promoted && (is_development_devicegroup_deployment || is_pre_dut_devicegroup_deployment || is_dut_devicegroup_deployment);
                    };
                    break;
                case views_devicegroup.TYPES_MAP.pre_dut:
                    // If view is dut devicegroup, only show deployments in the same product,
                    // that are promoted, and are from pre_dut, dut, development_devicegroup
                    view.filter = function (deployment) {
                        const devicegroup = deployment.rel.get('devicegroup');
                        const product = deployment.rel.get('product');
                        const is_same_product = product && product.id === this.getOption('product').id;
                        const is_development_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.development;
                        const is_pre_dut_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.pre_dut;
                        const is_dut_devicegroup_deployment = devicegroup && devicegroup.type === views_devicegroup.TYPES_MAP.dut;

                        return is_same_product && (is_development_devicegroup_deployment || is_pre_dut_devicegroup_deployment || is_dut_devicegroup_deployment);
                    };
                    break;

                default:
                    // This case should never happen
                    view.filter = function (deployment) {
                        const product = deployment.rel.get('product');

                        const is_same_product = product && product.id === this.getOption('product').id;

                        return is_same_product;
                    };
                    break;
            }
        } 

        if (this.getOption('deployment') && this.getOption('deployment').type === 'deployment') {
            this.chooseDeployment(this.getOption('deployment'));
        } else {
            this.showChildView('code_list', view);
            this.showChildView('pagination_controls', this.options.pagination_controls);
        }

    },
    newDeploymentSubmit: function(e) {
        var device_code = this.deployment.get('device_code');
        var agent_code = this.deployment.get('agent_code');
        var sha = this.deployment.get('sha');
        var description = this.ui.description.val().trim();
        var upgrade_option = this.ui.upgrade_option.filter(':checked').val();
        var totp = this.devicegroup.requires_mfa();
        var self = this;
        var headers;

        var attributes = {
            description: description,
        };

        // If agent/device code is unavailable, use sha
        if (sha) {
            attributes.sha = sha;
        } else {
            attributes.device_code = device_code;
            attributes.agent_code = agent_code;
        }

        if (totp) {
            headers = {
                'x-electricimp-otp-token': this.ui.totp_input.val()
            };
        }

        e.preventDefault();

        this.ui.load_spinner.show();

        this.collection.create(
            {
                type: 'deployment',
                attributes: attributes,
                relationships: {
                    devicegroup: {
                        type: this.devicegroup.type,
                        id: this.devicegroup.get('id'),
                    }
                }
            },
            {
                wait: true,
                headers: headers,
                url: Api.urls.deployments_nocode({ nocode: true }),
                success: function(new_deployment) {
                    self.closeDialog();
                    var sha = self.deployment.get('sha');
                    var short_sha = Handlebars.compile('{{short_sha \''+sha+'\'}}')();
                    var message = [
                        strings.success_1,
                        self.devicegroup.get('name'),
                        strings.success_2,
                        short_sha].join(' ')
                    ;
                    if (self.devicegroup && self.devicegroup.type.indexOf('dut') === -1 ) {
                        switch (upgrade_option) {
                            case 'conditional':
                                self.devicegroup.conditional_restart();
                                break;
                            case 'forced':
                                self.devicegroup.set_minimum_deployment(new_deployment).then(self.devicegroup.restart);
                                break;
                            default:
                                self.on_error(null, '');
                        }
                    }
                    globalCh.request('layout_alert', message, 'success', 4000);
                },
                error: function(model, response) {
                    this.handleError(response);
                }.bind(this)
            }
        );
    },
    handleError: function(err, message) {
        var error = err.responseJSON.errors[0];
        var title = error.title;
        if ( error.code === 'CX005' && error.meta && error.meta[0] && error.meta[0].text
             && error.meta[0].text.indexOf('invalid constant') > -1 ) {
            message = `You have referenced an Environment Variable in your
                       code which has not been added to this Device Group.
                       This can be done from Device Group > Settings`;
        }
        this.ui.load_spinner.hide();
        alertsCh.request('response_dialog', err, 'danger', title, message);
    }
});
export default view;

